import { useContext } from "react";
import { EXPECTED_YEARS_TO_RECEIVE_PENSION_OVER, LAST_SECTION_INDEX, MIN_AGE_CLIENT, MONTHS_IN_YEAR } from "../../../config/constants";
import { PensionContext } from "../../../contexts/pension-robot/PensionContext";
import { useNetCalculation } from "../../../hooks/netGrossCalculations";
import { useGetPerMonthFactorValues } from "../../../hooks/useGetPerMonthFactorValues";
import { getPerMonthFactor, isGoalAchieved } from "../../../utils/functions";
import { useAgePartnerAtRetirement, useAgeSelfAtRetirement, useIsJointCalculation, useLegalRetirementAge } from "./usePensionDate";

// ROBO-844: calculate surplus to resolve missing AOW, only all involved have a surplus
export const useSurplusAvailableToSolveMissingAow = () => {
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    const legalRetirementAge = useLegalRetirementAge();
    const ageOfSelfAtRetirementAgePartner = useAgeSelfAtRetirement(true);
    const ageOfPartnerAtRetirementAgeSelf = useAgePartnerAtRetirement(false);
    const shortageOrSurplus = useShortageOrSurplus();
    const joinedCalculation = useIsJointCalculation();

    const alwaysSurplus = joinedCalculation
        ? shortageOrSurplus.goalAchieved.single
        : shortageOrSurplus.goalAchieved.together

    // This calculation is only valid if for the full retirement there is a surplus
    if (!alwaysSurplus)
        return undefined;

    // Column header Andy Excel (ROBO-844): Joined calculation
    const calculatedTogether = joinedCalculation;
    
    // Column header Andy Excel (ROBO-844): RetirementAge < LegalRetirementAge
    const selfEarlierWithPension = personalInfo.self.pensionAge < legalRetirementAge;

    // Column header Andy Excel (ROBO-844): RetirementAgePartner < LegalRetirementAge
    const partnerEarlierWithPension = personalInfo.partner.pensionAge < legalRetirementAge;

    // Column header Andy Excel (ROBO-844): RetirementAge < Age of customer at RetirementAgePartner < legalRetirementAge
    const selfRetiredWhenPartnerIsRetiredButYoungerThanLegalRetirementAge = ageOfSelfAtRetirementAgePartner !== undefined
        && ageOfSelfAtRetirementAgePartner > personalInfo.self.pensionAge 
        && ageOfSelfAtRetirementAgePartner < legalRetirementAge;

    // Column header Andy Excel (ROBO-844): RetirementAgePartner < Age of partner at RetirementAge < legalRetirementAge
    const partnerRetiredWhenSelfIsRetiredButYoungerThanLegalRetirementAge = ageOfPartnerAtRetirementAgeSelf !== undefined
        && ageOfPartnerAtRetirementAgeSelf > personalInfo.partner.pensionAge 
        && ageOfPartnerAtRetirementAgeSelf < legalRetirementAge;

    // Scenario 1: calculation for Customer only and Customer is NOT entitled to AOW on retirement age
    if ( !calculatedTogether && selfEarlierWithPension ){
        // (LegalRetirementAge - RetirementAge) * <yearly surplus amount>
        const yearsUntilPensionSelf = (legalRetirementAge - personalInfo.self.pensionAge);
        return yearsUntilPensionSelf * shortageOrSurplus.selfShortageOrSurplusPerYear;
    }

    // Quick return if values are not present yet
    if (!ageOfSelfAtRetirementAgePartner || !ageOfPartnerAtRetirementAgeSelf) {
        return 0;
    }

    // Scenario 2: Joined calculation and Customer is NOT entitled to AOW on the chosen retirement age but partner 
    // is entitled to AOW on the chosen retirement age of partner and the surplus amount stays the same between the 
    // chosen retirement date of the customer and the legal retirement date of the customer
    if ( calculatedTogether
        && selfEarlierWithPension
        && !partnerEarlierWithPension
        && !selfRetiredWhenPartnerIsRetiredButYoungerThanLegalRetirementAge
    ) {
        // (LegalRetirementAge - RetirementAge) * <yearly surplus amount on retirement date customer>
        const yearsUntilPensionSelf = (legalRetirementAge - personalInfo.self.pensionAge);
        return yearsUntilPensionSelf * shortageOrSurplus.selfShortageOrSurplusPerYear;
    }

    // Scenario 3: Joined calculation and Customer is NOT entitled to AOW on retirement age but partner is entitled 
    // to AOW on retirement age of partner and the surplus amount is changed on the retirement date of the partner 
    // because this date lies after the chosen retirement age of the customer and before the legal retirement date of the customer
    if (calculatedTogether
        && selfEarlierWithPension
        && !partnerEarlierWithPension
        && selfRetiredWhenPartnerIsRetiredButYoungerThanLegalRetirementAge
    ){
        // (<Age of customer at RetirementAgePartner> - RetirementAge) * <yearly surplus amount on retirement date customer> +
        const selfPart = (ageOfSelfAtRetirementAgePartner - personalInfo.self.pensionAge) * shortageOrSurplus.selfShortageOrSurplusPerYear;

        // (LegalRetirementAge - <Age of customer at RetirementAgePartner>) * <yearly surplus amount on retirement date partner></yearly>
        const partnerPart = (legalRetirementAge - ageOfSelfAtRetirementAgePartner) * shortageOrSurplus.partnerShortageOrSurplusPerYear

        return selfPart + partnerPart;
    }

    // Scnario 4: Joined calculation and Customer is entitled to AOW on the chosen retirement age but partner is NOT 
    // entitled to AOW on the chosen retirement age of partner and the surplus amount stays the same between the retirement
    // date of the partner and the legal retirement date of the partner
    if ( calculatedTogether
        && !selfEarlierWithPension
        && partnerEarlierWithPension
        && !partnerRetiredWhenSelfIsRetiredButYoungerThanLegalRetirementAge
    ){
        // (LegalRetirementAge - RetirementAgePartner) * <yearly surplus amount on retirement date partner>
        const yearsUntilPensionPartner = legalRetirementAge - personalInfo.partner.pensionAge;
        return yearsUntilPensionPartner * shortageOrSurplus.partnerShortageOrSurplusPerYear;
    }

    // Scenario 5: Joined calculation and Customer is entitled to AOW on the chosen retirement age but partner is NOT entitled 
    // to AOW on the chosen retirement age of partner and the surplus amount is changed on the retirement date of the customer
    // because this date lies after the chosen retirement age of the partner and before the legal retirement date of the partner
    if ( calculatedTogether
        && !selfEarlierWithPension
        && partnerEarlierWithPension
        && partnerRetiredWhenSelfIsRetiredButYoungerThanLegalRetirementAge
    ){
        // (<Age of partner at RetirementAge> - RetirementAgePartner) * <yearly surplus amount on retirement date partner> +
        const partnerPart = (ageOfPartnerAtRetirementAgeSelf - personalInfo.partner.pensionAge) * shortageOrSurplus.partnerShortageOrSurplusPerYear;

        // (LegalRetirementAge - <Age of partner at RetirementAge>) * <yearly surplus amount on retirement date customer></yearly>
        const selfPart = (legalRetirementAge - ageOfPartnerAtRetirementAgeSelf) * shortageOrSurplus.selfShortageOrSurplusPerYear;
        return selfPart + partnerPart;
    }

    // Scenario 6: Joined calculation and Customer is NOT entitled to AOW on retirement age and partner is NOT entitled to AOW
    // on retirement age of partner
    if ( calculatedTogether
        && selfEarlierWithPension
        && partnerEarlierWithPension
        && !selfRetiredWhenPartnerIsRetiredButYoungerThanLegalRetirementAge
        && !partnerRetiredWhenSelfIsRetiredButYoungerThanLegalRetirementAge
    ){
        // (<Number of years between earliest and latest retirement date>) * <yearly surplus amount on earliest retirement date > +
        // (<Number of years between latest retirement date and latest legal retirement date>) * <yearly surplus amount on latest retirement date ></yearly>
        if (personalInfo.partner.age && personalInfo.self.age){
            const yearsPartnerToPension = personalInfo.partner.pensionAge - personalInfo.partner.age;
            const yearsSelfToPension = personalInfo.self.pensionAge - personalInfo.self.age;
    
            if ( (yearsSelfToPension ) <= (yearsPartnerToPension)){
                const yearsBetweenEarliestAndLatestRetirement = yearsPartnerToPension - yearsSelfToPension
                const yearsBetweenLatestAndLegalRetirement = legalRetirementAge - personalInfo.partner.pensionAge;
                return yearsBetweenEarliestAndLatestRetirement * shortageOrSurplus.selfShortageOrSurplusPerYear
                    + yearsBetweenLatestAndLegalRetirement * shortageOrSurplus.partnerShortageOrSurplusPerYear;
            } else {
                const yearsBetweenEarliestAndLatestRetirement = yearsSelfToPension - yearsPartnerToPension;
                const yearsBetweenLatestAndLegalRetirement = legalRetirementAge-personalInfo.self.pensionAge;
                return yearsBetweenEarliestAndLatestRetirement * shortageOrSurplus.partnerShortageOrSurplusPerYear
                    + yearsBetweenLatestAndLegalRetirement * shortageOrSurplus.selfShortageOrSurplusPerYear
            }
        }
    }

    return undefined;
};

const useYearsPartnerExpectedToReceivePension = () => {
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);
    const retirementForecastQueryPartner = useGetPerMonthFactorValues(personalInfo.partner?.age ?? MIN_AGE_CLIENT);
    const yearsPartnerExpectedToReceivePension = getPerMonthFactor(retirementForecastQueryPartner.data) / MONTHS_IN_YEAR;
    return yearsPartnerExpectedToReceivePension;
}

const useYearsSelfExpectedToReceivePension = () => {
    const {
        parameters: {personalInfo},
    } = useContext(PensionContext);
    const retirementForecastQuery = useGetPerMonthFactorValues(personalInfo.self?.age ?? MIN_AGE_CLIENT);
    const yearsSelfExpectedToReceivePension = getPerMonthFactor(retirementForecastQuery.data) / MONTHS_IN_YEAR;
    return yearsSelfExpectedToReceivePension;
}

export const useShortageOrSurplus = () =>{
    const {
        parameters: { expensesInfo },
    } = useContext(PensionContext);

    const netValuesAtRetirementAgeOfSelf = useNetCalculation(LAST_SECTION_INDEX, false);
    const netValuesAtRetirementAgeOfPartner = useNetCalculation(LAST_SECTION_INDEX, true);

    const goal = expensesInfo?.goal ?? 0;

    const totalNetIncomeMonthlySelfPerspective = netValuesAtRetirementAgeOfSelf.netIncome.total.monthly;
    const totalNetIncomeMonthlyPartnerPerspective = netValuesAtRetirementAgeOfPartner.netIncome.total.monthly;

    const goalAchiedAtPartnerPensionAge = isGoalAchieved(goal, totalNetIncomeMonthlyPartnerPerspective);
    const goalAchievedAtSelfPensionAge = isGoalAchieved(goal, totalNetIncomeMonthlySelfPerspective);

    const selfShortageOrSurplusPerYear = goalAchievedAtSelfPensionAge.differenceRaw * MONTHS_IN_YEAR;
    const partnerShortageOrSurplusPerYear = goalAchiedAtPartnerPensionAge.differenceRaw * MONTHS_IN_YEAR;

    return {
        goal,
        goalAchieved: {
            single: goalAchievedAtSelfPensionAge.achieved,
            together: goalAchiedAtPartnerPensionAge.achieved && goalAchievedAtSelfPensionAge.achieved
        },
        selfShortageOrSurplusPerYear,
        partnerShortageOrSurplusPerYear
    }
}

export const useTotalShortage = () => {
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);
    const isCalculcatedTogether = useIsJointCalculation();
    const shortageOrSurplus = useShortageOrSurplus();
    const yearsSelfExpectedToReceivePension = useYearsSelfExpectedToReceivePension();
    const yearsPartnerExpectedToReceivePension = useYearsPartnerExpectedToReceivePension();

    const selfShortagePerYear = shortageOrSurplus.selfShortageOrSurplusPerYear < 0
        ? Math.abs(shortageOrSurplus.selfShortageOrSurplusPerYear)
        : 0;

    const partnerShortagePerYear = shortageOrSurplus.partnerShortageOrSurplusPerYear < 0
        ? Math.abs(shortageOrSurplus.partnerShortageOrSurplusPerYear)
        : 0;

    if (!isCalculcatedTogether){
        const selfFullShortageOverPension = selfShortagePerYear * yearsSelfExpectedToReceivePension;
        return Math.abs(selfFullShortageOverPension);
    }

    const yearsToPensionAgeSelf = personalInfo.self.pensionAge - personalInfo.self.age!;
    const yearsToPensionAgePartner = personalInfo.partner.pensionAge - personalInfo.partner.age!;

    const diffYearsBetweenPensionDates = Math.min(Math.abs(yearsToPensionAgeSelf - yearsToPensionAgePartner), EXPECTED_YEARS_TO_RECEIVE_PENSION_OVER);

    const selfGoesWithPensionFirst = yearsToPensionAgeSelf < yearsToPensionAgePartner;
    const selfYearsUntilPensionChange = selfGoesWithPensionFirst
        ? diffYearsBetweenPensionDates
        : EXPECTED_YEARS_TO_RECEIVE_PENSION_OVER - diffYearsBetweenPensionDates;

    const selfRatioOfShortage = selfYearsUntilPensionChange
        ? selfYearsUntilPensionChange / EXPECTED_YEARS_TO_RECEIVE_PENSION_OVER
        : 0;

    const partnerYearsUntilPensionChange = selfGoesWithPensionFirst
        ? EXPECTED_YEARS_TO_RECEIVE_PENSION_OVER - diffYearsBetweenPensionDates
        : diffYearsBetweenPensionDates;

    const partnerRatioOfShortage = partnerYearsUntilPensionChange
        ? partnerYearsUntilPensionChange / EXPECTED_YEARS_TO_RECEIVE_PENSION_OVER
        : 0;

    const selfPartOfShortage = selfShortagePerYear * yearsSelfExpectedToReceivePension * selfRatioOfShortage;
    const partnerPartOfShortage = partnerShortagePerYear * yearsPartnerExpectedToReceivePension * partnerRatioOfShortage;

    const fullShortageOverPensionPeriod = Math.abs(selfPartOfShortage + partnerPartOfShortage);
    return fullShortageOverPensionPeriod
}